import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from "react-router-dom";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlank from "@mui/icons-material/CheckBoxOutlineBlank";
import Star from "@mui/icons-material/Star";
import StarBorder from "@mui/icons-material/StarBorder";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";

import { Fab, Tooltip, IconButton } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  ModelBase, 
  PlayerObject,
  UpsertableBase,
} from "@models";
import { DataType } from '@dndModels/Types';

import { makeStyles } from "@utilities/theme_utilities";
import DandDAPI from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from '@dndUtilities/char_utilities';

import CopyButton from "@components/input/buttons/icon_buttons/CopyButton";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import ArchiveButton from "@components/input/buttons/icon_buttons/ArchiveButton";
import LoadingBox from "@components/input/LoadingBox";
import { Campaign, CampaignPlayer } from '@dndModels';

interface Props { 
  data_type: DataType;
  obj: UpsertableBase;
  disabled: boolean;
  onProcessingChange: (processing: boolean) => void;
  onDelete: (_id: string) => void;
  special_copy?: (obj: UpsertableBase) => void;
  special_update?: (obj: UpsertableBase, onSuccess: () => void) => void;
  refresh?: () => void;
  feature_source_type?: string;
  no_edit?: boolean;
  details?: boolean;
  constants?: any;
  extra?: (obj: UpsertableBase) => React.ReactNode;
  extra_details?: (obj: UpsertableBase) => string;
  extra_buttons?: (obj: UpsertableBase) => React.ReactNode;
  name_display?: (obj: UpsertableBase) => React.ReactNode;
  show_image?: boolean;
  onArchiveChange: () => void;
  extra_fields?: Array<{
    label: string;
    field: string;
    display?: (obj: UpsertableBase) => React.ReactNode;
  }>;
  log_it?: boolean;
}

const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const api = DandDAPI.getInstance();

export default function ObjectIndexItem({
  data_type,
  obj,
  disabled,
  constants = {},
  onProcessingChange,
  onDelete,
  special_copy,
  special_update,
  refresh,
  extra,
  extra_details,
  extra_buttons,
  name_display,
  feature_source_type = "",
  no_edit = false,
  details = false,
  show_image = false,
  onArchiveChange,
  extra_fields = [],
  log_it
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prevDisabled, setPrevDisabled] = useState(disabled);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [detail, setDetail] = useState("");

  useEffect(() => {
    if(disabled !== prevDisabled) {
      setPrevDisabled(disabled);
    }
  }, [disabled, prevDisabled]);

  const load = useCallback( async() => {
    const display_string = obj.detailFormat(); // obj.detail_format;
    if (display_string.includes("{list_of_type")) {
      let data_types = common_util.extractListTypes(display_string);
      const smart_hash_keys = Object.keys(api.smart_hash);
      const temp_hash_keys = Object.keys(api.temp_hash);
      data_types = common_util.unique_array(data_types.filter(o => !smart_hash_keys.includes(o) && !temp_hash_keys.includes(o)));

      if (data_types.length > 0) {
        setLoading(true);
        await api.getSetOfObjects({ data_types });
        setLoading(false);
      }
    }
    let new_detail = char_util.getStringWithReplacementsWithObject({ clean_me: obj.detailFormat(), backup: "", obj, constants });
    
    if (extra_details) {
      const the_extra = extra_details(obj);
      if (the_extra.length > 0) {
        new_detail += " (" + the_extra + ")";
      }
    }

    if (obj instanceof ModelBase && api.user && api.user.admin && obj.blocked) {
      new_detail += " - Blocked";
    }
    setDetail(new_detail.trim());
  }, [obj, api.user, constants]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  const view_only = !api.user || (obj instanceof PlayerObject && obj.owner_id !== api.user._id && !api.user.admin);
  const true_disabled = api.user === null || (!api.user.admin && obj instanceof ModelBase && obj.owner_id !== api.user._id);

  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
      expandedContent: {
        backgroundColor: '#000',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        padding: '8px',
        marginTop: '8px',
      },
    });
  });

  const { classes: rowClasses, cx } = useRowStyles();

  let linkUrl = obj.linkURL;
  if (linkUrl === "") {
    linkUrl = `/${data_type}/${obj._id}`;
  }

  // Calculate grid sizes based on extra_fields
  const hasExtraFields = extra_fields.length > 0;
  const nameSize = hasExtraFields ? 12 - (extra_fields.length * 2) : 12;

  let expanded_content: any = null;
  if (expanded) {
    const description = char_util.getStringWithReplacementsWithObject({ 
      clean_me: "{obj.description}", 
      backup: "", 
      obj, 
      constants 
    });

    if (extra || extra_buttons) {
      if (log_it) {
        debugger;
      }
      expanded_content = (
        <Grid size={12} className={rowClasses.expandedContent}>
          <Grid container spacing={0} direction="row">
            <Grid size={12} className="padded">
              <Link to={linkUrl} style={{ color: 'inherit', fontWeight: "bold", textDecoration: "none" }}>
                {obj.name}
              </Link>
            </Grid>
            { detail.length > 0 && 
              <Grid size={12} className="padded small_label gray">
                {detail}
              </Grid> 
            }
            { show_image && obj.image_url !== "" &&
              <Grid size={12} className="padded">
                <img src={ obj.image_url } alt={obj.name} style={{ maxHeight: "100px", maxWidth: "100px" }} />
              </Grid>
            }
            { api.true_user && 
              <>
                { obj instanceof PlayerObject ?
                  <>
                    { (api.true_user.is_admin || obj.owner_id === api.true_user._id) &&
                      <>
                        <Grid size={12}
                          container spacing={0} direction="row">
                          <Grid size={6}>
                            <Grid container spacing={0} direction="row">
                              <Grid size={4}>
                                <Tooltip title={`Copy ${obj.name}`}>
                                  <span>
                                    <CopyButton
                                      label={`Copy`}
                                      disabled={ 
                                        disabled || 
                                        view_only || 
                                        api.user === null || 
                                        !(api.user.admin || 
                                          (obj.owner_id === api.user._id || 
                                            (obj instanceof ModelBase && 
                                              (common_util.anyOverlaps(["",...api.user.source_book_ids], obj.source_book_ids) || obj.free)
                                            )
                                          )
                                        )
                                      } 
                                      onClick={ async () => {
                                        // debugger;
                                        onProcessingChange(true);
                                        if (special_copy === undefined) {
                                          // debugger;
                                          const user = api.user;
                                          if (user) {
                                            const new_obj = obj.clone();
                                            new_obj.name = new_obj.name + " (Copy)";
                                            new_obj._id = "";
                                            const res = await api.createObject(data_type, new_obj);
                                            onProcessingChange(false);
                                            if (res) {
                                              setNavigateTo(`/${data_type}/${res.body.id}`);
                                            }
                                          }
                                        } else {
                                          special_copy(obj);
                                        }
                                      }}
                                    />
                                  </span>
                                </Tooltip> 
                              </Grid>
                              <Grid size={4}>
                                <Tooltip title={`${obj.is_archived ? "Unarchive" : "Archive"} ${obj.name}`}>
                                  <span>
                                    <ArchiveButton
                                      label={`${obj.is_archived ? "Unarchive" : "Archive"}`}
                                      is_archived={obj.is_archived}
                                      disabled={disabled || true_disabled || view_only || !api.online}
                                      onClick={ () => {
                                        obj.is_archived = !obj.is_archived;
                                        if (data_type !== "source_book" && obj instanceof ModelBase) {
                                          // obj.dominance_level = 0;
                                          if (!obj.archived) {
                                            obj.source_book_abbrev = "Recently Unarchived";
                                          }
                                        }
                                        onProcessingChange(true);
                                        if (special_update === undefined) {
                                          api.updateObject(obj.data_type, obj).then(res => {
                                            onProcessingChange(false);
                                            onArchiveChange();
                                            if (refresh) {
                                              refresh();
                                            }
                                          });
                                        } else {
                                          special_update(obj, () => {
                                            onProcessingChange(false);
                                            onArchiveChange();
                                            if (refresh) {
                                              refresh();
                                            }
                                          });
                                        }
                                      }}
                                    />
                                  </span>
                                </Tooltip> 
                              </Grid>
                              <Grid size={4}>
                                <Tooltip title={`Delete ${obj.name}`}>
                                  <span>
                                    <DeleteButton 
                                      label="Delete"
                                      confirm_message={`Are you sure you want to delete ${obj.name}? This cannot be undone.`}
                                      disabled={disabled || true_disabled || view_only}
                                      onClick={ () => {
                                        onProcessingChange(true);
                                        api.deleteObject(data_type, obj).then(res => {
                                          onDelete(obj._id);
                                          onProcessingChange(false);
                                        });
                                      }}
                                    />
                                  </span>
                                </Tooltip> 
                              </Grid>
                            </Grid>
                          </Grid>
                          <Grid size={6}>
                            { extra_buttons && extra_buttons(obj) }
                          </Grid>
                          { extra && 
                            <Grid size={12}>{ extra(obj) }</Grid>
                          }
                        </Grid>
                      </>
                    }
                  </>
                : (api.true_user.is_admin) &&
                  <>
                    <Grid size={12}
                      container spacing={0} direction="row">
                      <Grid size={6}>
                        <Grid container spacing={0} direction="row">
                          <Grid size={8}>
                            { (obj instanceof ModelBase) && 
                              <Tooltip title={`Copy ${obj.name}`}>
                                <span>
                                  <CopyButton
                                    label={`Copy`}
                                    disabled={ 
                                      disabled || 
                                      view_only || 
                                      api.user === null || 
                                      !(api.user.admin || 
                                        (obj.owner_id === api.user._id || 
                                          (common_util.anyOverlaps(["",...api.user.source_book_ids], obj.source_book_ids) || obj.free)
                                        )
                                      )
                                    } 
                                    onClick={ async () => {
                                      // debugger;
                                      onProcessingChange(true);
                                      if (special_copy === undefined) {
                                        // debugger;
                                        const user = api.user;
                                        if (user) {
                                          await api.loadObject(obj);
                                          const new_obj = obj.clone();
                                          new_obj.name = new_obj.name + " (Copy)";
                                          new_obj._id = "";
                                          if (obj instanceof ModelBase && new_obj instanceof ModelBase) {
                                            new_obj.template_only = false;
                                            if (!user.admin) {
                                              new_obj.owner_id = user._id;
                                              new_obj.source_books_old = [{ type: "Homebrew", id: user._id, third_party: false }];
                                            } else {
                                              api.applyWorkingOn(new_obj);
                                            }
                                            new_obj.variant_of_id = obj.variant_group_id;
                                          }
                                          const res = await api.createObject(data_type, new_obj);
                                          onProcessingChange(false);
                                          if (res) {
                                            setNavigateTo(`/${data_type}/${res.body.id}`);
                                          }
                                        }
                                      } else {
                                        special_copy(obj);
                                      }
                                    }}
                                  />
                                </span>
                              </Tooltip> 
                            }
                          </Grid>
                          <Grid size={4}>
                            <Tooltip title={`Delete ${obj.name}`}>
                              <span>
                                <DeleteButton 
                                  label="Delete"
                                  confirm_message={`Are you sure you want to delete ${obj.name}? This cannot be undone.`}
                                  disabled={disabled || true_disabled || view_only}
                                  onClick={ () => {
                                    onProcessingChange(true);
                                    api.deleteObject(data_type, obj).then(res => {
                                      onDelete(obj._id);
                                      onProcessingChange(false);
                                    });
                                  }}
                                />
                              </span>
                            </Tooltip> 
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid size={6}>
                        { extra_buttons && extra_buttons(obj) }
                      </Grid>
                      { extra && 
                        <Grid size={12}>{ extra(obj) }</Grid>
                      }
                    </Grid>
                  </>
                }
              </>
            }
          </Grid>
        </Grid>
      );
    } else {
      expanded_content = (
        <Grid size={12} className={rowClasses.expandedContent}>
          <Grid container spacing={0} direction="row">
            <Grid size={12} className="padded">
              <Link to={linkUrl} style={{ color: 'inherit', fontWeight: "bold", textDecoration: "none" }}>
                {obj.name}
              </Link>
            </Grid>
            { detail.length > 0 && 
              <Grid size={12} className="padded small_label gray">
                {detail}
              </Grid> 
            }
            { show_image && obj.image_url !== "" &&
              <Grid size={12} className="padded">
                <img src={ obj.image_url } alt={obj.name} style={{ maxHeight: "100px", maxWidth: "100px" }} />
              </Grid>
            }
            { api.user && 
              <>
                { obj instanceof PlayerObject ?
                  <>
                    { (api.user.admin || obj.owner_id === api.user._id) &&
                      <Grid size={12}
                        container spacing={0} direction="row">
                        <Grid size={4}>
                          <Tooltip title={`Copy ${obj.name}`}>
                            <span>
                              <CopyButton
                                label={`Copy`}
                                disabled={ 
                                  disabled || 
                                  view_only || 
                                  api.user === null || 
                                  !(api.user.admin || obj.owner_id === api.user._id)
                                } 
                                onClick={ async () => {
                                  onProcessingChange(true);
                                  if (special_copy === undefined) {
                                    const user = api.user;
                                    if (user) {
                                      const new_obj = obj.clone();
                                      new_obj.name = new_obj.name + " (Copy)";
                                      new_obj._id = "";
                                      const res = await api.createObject(data_type, new_obj);
                                      onProcessingChange(false);
                                      if (res) {
                                        setNavigateTo(`/${data_type}/${res.body.id}`);
                                      }
                                    }
                                  } else {
                                    special_copy(obj);
                                  }
                                }}
                              />
                            </span>
                          </Tooltip> 
                        </Grid>
                        <Grid size={{ xs: 4, sm: 2 }}>
                          <Tooltip title={`${obj.is_archived ? "Unarchive" : "Archive"} ${obj.name}`}>
                            <span>
                              <ArchiveButton
                                label={`${obj.is_archived ? "Unarchive" : "Archive"}`}
                                is_archived={obj.is_archived}
                                disabled={disabled || true_disabled || view_only || !api.online}
                                onClick={ () => {
                                  obj.is_archived = !obj.is_archived;
                                  if (data_type !== "source_book" && obj instanceof ModelBase) {
                                    // obj.dominance_level = 0;
                                    if (!obj.archived) {
                                      obj.source_book_abbrev = "Recently Unarchived";
                                    }
                                  }
                                  onProcessingChange(true);
                                  if (special_update === undefined) {
                                    api.updateObject(obj.data_type, obj).then(res => {
                                      onProcessingChange(false);
                                      onArchiveChange();
                                      if (refresh) {
                                        refresh();
                                      }
                                    });
                                  } else {
                                    special_update(obj, () => {
                                      onProcessingChange(false);
                                      onArchiveChange();
                                      if (refresh) {
                                        refresh();
                                      }
                                    });
                                  }
                                }}
                              />
                            </span>
                          </Tooltip> 
                        </Grid>
                        <Grid size={{ xs: 4, sm: 3 }}>
                          <Tooltip title={`Delete ${obj.name}`}>
                            <span>
                              <DeleteButton 
                                label="Delete"
                                confirm_message={`Are you sure you want to delete ${obj.name}? This cannot be undone.`}
                                disabled={disabled || true_disabled || view_only}
                                onClick={ () => {
                                  onProcessingChange(true);
                                  api.deleteObject(data_type, obj).then(res => {
                                    onDelete(obj._id);
                                    onProcessingChange(false);
                                  });
                                }}
                              />
                            </span>
                          </Tooltip> 
                        </Grid>
                      </Grid>
                    }
                  </>
                : obj instanceof ModelBase && (api.user.admin || obj.owner_id === api.user._id) &&
                  <Grid size={12}
                    container spacing={0} direction="row">
                    <Grid size={{ xs: 8, sm: 6 }}>
                      <Tooltip title={`Copy ${obj.name}`}>
                        <span>
                          <CopyButton
                            label={`Copy`}
                            disabled={ 
                              disabled || 
                              view_only || 
                              api.user === null || 
                              !(api.user.admin || 
                                (obj.owner_id === api.user._id || 
                                  common_util.anyOverlaps(["",...api.user.source_book_ids], obj.source_book_ids)
                                )
                              )
                            } 
                            onClick={ async () => {
                              onProcessingChange(true);
                              if (special_copy === undefined) {
                                const user = api.user;
                                if (user) {
                                  await api.loadObject(obj);
                                  const new_obj = obj.clone();
                                  new_obj.name = new_obj.name + " (Copy)";
                                  new_obj._id = "";
                                  if (obj instanceof ModelBase && new_obj instanceof ModelBase) {
                                    if (!user.admin) {
                                      new_obj.owner_id = user._id;
                                      new_obj.source_books_old = [{ type: "Homebrew", id: user._id, third_party: false }];
                                    } else {
                                      api.applyWorkingOn(new_obj);
                                    }
                                    new_obj.variant_of_id = obj.variant_group_id;
                                  }
                                  const res = await api.createObject(data_type, new_obj);
                                  onProcessingChange(false);
                                  if (res) {
                                    setNavigateTo(`/${data_type}/${res.body.id}`);
                                  }
                                }
                              } else {
                                special_copy(obj);
                              }
                            }}
                          />
                        </span>
                      </Tooltip> 
                    </Grid>
                    <Grid size={{ xs: 4, sm: 3 }}>
                      <Tooltip title={`Delete ${obj.name}`}>
                        <span>
                          <DeleteButton 
                            label="Delete"
                            confirm_message={`Are you sure you want to delete ${obj.name}? This cannot be undone.`}
                            disabled={disabled || true_disabled || view_only}
                            onClick={ () => {
                              onProcessingChange(true);
                              api.deleteObject(data_type, obj).then(res => {
                                onDelete(obj._id);
                                onProcessingChange(false);
                              });
                            }}
                          />
                        </span>
                      </Tooltip> 
                    </Grid>
                  </Grid>
                }
              </>
            }
            {description && (
              <Grid size={12} className="padded" style={{ whiteSpace: 'pre-wrap' }}>
                {description}
              </Grid>
            )}
          </Grid>
        </Grid>
      );
    }
  }

  const renderNameDisplay = () => {
    if (name_display) {
      return name_display(obj);
    } else {
      if (obj.display_format.length > 0) {
        return obj.display_format;
      } else {
        return "Nameless";
      }
    }
  }

  const renderFavorite = () => {
    if (api.user) {
      if (obj instanceof PlayerObject && api.user._id === obj.owner_id) {
        return (
          <Grid size={{ xs: 2, sm: 1 }}>
            <Tooltip title={`Make ${obj.name} a favorite`}>
              <span>
                <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                  disabled={disabled || view_only || true_disabled || !api.online}
                  onClick={ () => {
                    obj.is_favorite = !obj.is_favorite;
                    onProcessingChange(true);
                    if (special_update === undefined) {
                      api.updateObject(obj.data_type, obj).then(res => {
                        onProcessingChange(false);
                        if (refresh) {
                          refresh();
                        }
                      });
                    } else {
                      special_update(obj, () => {
                        onProcessingChange(false);
                        if (refresh) {
                          refresh();
                        }
                      });
                    }
                  }}>
                  { obj.is_favorite ? <Star/> : <StarBorder/> }
                </Fab>
              </span>
            </Tooltip>
          </Grid>
        );
      } else if (obj instanceof Campaign) {
        // Campaigns can still be shown for other users, but this will mean they're a player in the campaign.
        // In that case we store whether it's a favorite on their c_player.
        const c_player = api.user.c_players.find(o => o.campaign_id === obj._id);
        if (c_player) {
          return (
            <Grid size={{ xs: 2, sm: 1 }}>
              <Tooltip title={`Make ${obj.name} a favorite`}>
                <span>
                  <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                    disabled={!api.online}
                    onClick={ () => {
                      c_player.campaign_is_favorite = !c_player.campaign_is_favorite;
                      onProcessingChange(true);
                      if (special_update === undefined) {
                        api.updateObject(c_player.data_type, c_player).then(res => {
                          onProcessingChange(false);
                          if (refresh) {
                            refresh();
                          }
                        });
                      } else {
                        special_update(obj, () => {
                          onProcessingChange(false);
                          if (refresh) {
                            refresh();
                          }
                        });
                      }
                    }}>
                    { c_player.campaign_is_favorite ? <Star/> : <StarBorder/> }
                  </Fab>
                </span>
              </Tooltip>
            </Grid>
          );
        }
      }
    }
    return null;
  }
  
  if (navigateTo) {
    return <Navigate to={navigateTo} />;
  } else if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    return (
      <Grid container spacing={0} direction="row" className={cx(rowClasses.root)}>
        { renderFavorite() }
        <Grid size={1} className="padded">
          <IconButton 
            size="small"
            onClick={() => setExpanded(!expanded)}
            style={{ padding: '4px' }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Grid>
        <Grid
          className="padded"
          size={{
            xs: obj instanceof PlayerObject ? nameSize - 3 : nameSize - 1,
            sm: obj instanceof PlayerObject ? nameSize - 2 : nameSize - 1
          }}>
          <Grid container spacing={0} direction="row">
            <Grid size={12}>
              <Link to={linkUrl} style={{ color: 'inherit', textDecoration: 'none' }}>
                { renderNameDisplay() }
              </Link>
            </Grid>
            { detail.length > 0 && 
              <Grid
                className="padded small_label gray"
                size={{
                  xs: 12
                }}>
                {detail}
              </Grid> 
            }
          </Grid>
        </Grid>
        {extra_fields.map((field, index) => {
          // if (obj.name.includes("Acolyte")) {
          //   debugger;
          // }
          // if (field.field === "owned") {
          //   debugger;
          // }
          const value = field.display 
            ? field.display(obj) 
            : (obj as any)[field.field];
          return (
            <Grid size={2} key={index} className="padded centered">
              { typeof value === "boolean" ? (value ? <CheckBoxIcon /> : <CheckBoxOutlineBlank />) : value }
            </Grid>
          );
        })}
        { expanded_content }
      </Grid>
    );
  }
}